import { IAgoraRTCClient, IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { safeImportAgora } from "../LiveVideo/agoraHelper"
import { createRemoteAudioPlayback } from "../LiveVideo/remoteAudioPlayback"
import { AbstractVideoController, VideoControllerData } from "./AbstractVideoController"
import { config } from "@nova/configuration/browser"
import socketio, { Socket } from "socket.io-client"
import { MapRef } from "react-map-gl"
import { fixBadTelemetryData } from "../module"

type AgoraUser = IAgoraRTCRemoteUser

export class LiveVideoFlightAnonymousController extends AbstractVideoController {
    private client?: IAgoraRTCClient
    private agoraUser?: IAgoraRTCRemoteUser
    private mediaStreamTrack?: MediaStreamTrack
    private audioPlayback = createRemoteAudioPlayback()
    private streamIsInitialized = false
    private streamIsFinished = false
    private socket?: Socket
    private mapRef?: MapRef
    showTelemetry = false
    streamName = ""
    pilotName = ""

    constructor({ flightId }: { flightId: string }) {
        super({ flightId })
        const AgoraRTC = safeImportAgora()
        if (!AgoraRTC) {
            throw new Error("Tried to create Agora Client while Agora is unavailable")
        }
        this.client = AgoraRTC.createClient({ mode: "live", codec: "h264", role: "audience" })
        this.droneIconColor = droneIconColors[0]
        this.videoProgress = 1
    }

    override async initialize(signal: AbortSignal) {
        if (!this.client || this.streamIsFinished) return
        if (!this.streamIsInitialized) {
            await this.client.setClientRole("audience", { level: 1 })
            if (signal.aborted) {
                console.warn("Aborted before initialization")
                return
            }
            this.boundOnPublishAgoraEvent = this.onPublishAgoraEvent.bind(this)
            this.boundOnUnpublishAgoraEvent = this.onUnpublishAgoraEvent.bind(this)
            this.client.on("user-published", this.boundOnPublishAgoraEvent)
            this.client.on("user-unpublished", this.boundOnUnpublishAgoraEvent)
            const res = await fetch(`/api/live-video/${this.flightId}/get-watch-token`)
            const { token } = await res.json()
            await this.client.join(config.get("agoraId"), this.flightId, token, null)
            this.streamIsInitialized = true
        }
        if (!this.socket) {
            this.socket = socketio("", {
                withCredentials: true,
                transports: ["websocket"],
                path: "/api/socket",
                reconnectionDelay: 2000,
                timeout: 5000,
            })
            this.socket.on("livedroneevent", (payload) => {
                const { data, flightId } = JSON.parse(payload)
                if (this.flightId === flightId) {
                    this.pushData(data)
                }
            })
            this.boundOnSocketConnected = this.onSocketConnected.bind(this)
            this.socket.on("connected", this.boundOnSocketConnected)
        }
        if (signal.aborted) {
            console.warn("Aborted after initialization, deinitializing...")
            await this.deinitialize()
            return
        }
    }
    private boundOnSocketConnected?: typeof this.onSocketConnected
    private async onSocketConnected() {
        if (!this.socket) {
            return
        }
        this.socket.emit("subscribeToDroneEvents", this.flightId)
        await this.fetchAllData()
    }
    override async deinitialize() {
        if (!this.client) return
        if (this.streamIsInitialized) {
            this.client.off("user-published", this.boundOnPublishAgoraEvent!)
            this.client.off("user-unpublished", this.boundOnUnpublishAgoraEvent!)
            await this.onUnpublishAgoraEvent()
            await this.client.leave()
            this.streamIsInitialized = false
        }
        if (this.socket) {
            this.socket.emit("unsubscribeToDroneEvents", this.flightId)
            this.socket.disconnect()
        }
    }

    private boundOnPublishAgoraEvent?: typeof this.onPublishAgoraEvent
    private async onPublishAgoraEvent(
        user: AgoraUser,
        mediaType: "video" | "audio" | "datachannel"
    ) {
        if (!this.streamIsInitialized || !this.client) return
        if (mediaType === "video") {
            this.agoraUser = user
            await this.client.subscribe(user, mediaType)
            if (user.videoTrack) {
                this.mediaStreamTrack = user.videoTrack.getMediaStreamTrack()
                this.emit("videoTrackChanged")
            }
            this.refreshConnectivity()
            return
        }
        if (mediaType === "audio") {
            this.agoraUser = user
            this.audioPlayback.attach(await this.client.subscribe(user, mediaType))
            this.audioIsAvailable = true
            this.applyAudioSettings()
            this.emit("audioAvailabilityChanged")
            this.refreshConnectivity()
        }
    }

    private refreshConnectivity() {
        this.setStreamAvailability({
            cameraIsOff: !this.streamIsFinished && !this.mediaStreamTrack && this.audioIsAvailable,
            isDisconnected: false,
        })
    }
    private boundOnUnpublishAgoraEvent?: typeof this.onUnpublishAgoraEvent
    private async onUnpublishAgoraEvent(
        user?: AgoraUser,
        mediaType?: "video" | "audio" | "datachannel"
    ) {
        if (!this.streamIsInitialized || !this.client || !this.agoraUser) return
        if (!mediaType || mediaType === "video") {
            try {
                await this.client.unsubscribe(this.agoraUser, "video")
            } catch {}
            if (this.mediaStreamTrack) {
                this.mediaStreamTrack = undefined
                this.emit("videoTrackChanged")
            }
            this.refreshConnectivity()
        }
        if (!mediaType || mediaType === "audio") {
            this.audioPlayback.detach()
            try {
                await this.client.unsubscribe(this.agoraUser, "audio")
            } catch {}
            if (this.audioIsAvailable) {
                this.audioIsAvailable = false
                this.emit("audioAvailabilityChanged")
            }
            this.refreshConnectivity()
        }
    }
    protected override applyAudioSettings() {
        this.audioPlayback.apply({ muted: this.isMuted, volumePercent: this.volumePercent })
    }

    override attachToElement(element: HTMLVideoElement) {
        this.videoElement = element
        const attach = () => {
            if (this.mediaStreamTrack) {
                element.srcObject = new MediaStream([this.mediaStreamTrack])
                element.load()
                this.isPlaying = true
                this.hasPlayed = true
                this.emit("isPlayingChanged")
            } else {
                // Without this the element holds the last decoded frame instead of going black.
                element.srcObject = null
            }
        }
        this.on("videoTrackChanged", attach)
        attach()
        return () => {
            element.srcObject = null
            element.removeAttribute("srcObject")
            element.load()
            this.off("videoTrackChanged", attach)
        }
    }

    override async fetchAllData() {
        const statusRes = await fetch(`/api/protobuf/live-status/${this.flightId}`)
        const { status, telemetry, name, pilotName } = (await statusRes.json()) as {
            status: "NOT-LIVE" | "LIVE" | "NOT-STARTED"
            telemetry?: boolean
            name: string
            pilotName?: string
        }
        this.showTelemetry = !!telemetry
        this.streamName = name ?? ""
        this.pilotName = pilotName ?? ""
        if (status === "NOT-LIVE") {
            this.streamIsFinished = true
            void this.deinitialize()
        } else {
            const res = await fetch(`/api/live-video/${this.flightId}/get-anonymous-payload`)
            const { events } = await res.json()
            const sorted = [...events, ...this.data].sort((a, b) => a.timestamp - b.timestamp)
            const filtered = sorted.filter((e, i) => e.timestamp !== sorted?.[i - 1]?.timestamp)
            const fixed = fixBadTelemetryData(filtered)
            this.data = fixed
            this.hasFetched = true
        }
        this.emit("dataChanged")
    }

    override pushData(data: VideoControllerData) {
        if (!data.longitude || !data.latitude) {
            for (let i = this.data.length - 1; i >= 0; i--) {
                if (this.data[i].latitude && this.data[i].longitude) {
                    data.latitude = this.data[i].latitude
                    data.longitude = this.data[i].longitude
                    break
                }
            }
        }
        const lastDataItem = this.data.at(-1)
        if (!lastDataItem || (data.createdAt || 0) > (lastDataItem.createdAt || 0)) {
            this.data.push(data)
        } else {
            this.data = [...this.data, data].toSorted((a, b) => a.timestamp - b.timestamp)
        }
        this.emit("dataChanged")
    }

    attachMapRef(mapRef: MapRef) {
        this.mapRef = mapRef
        this.boundZoomToCurrentFrame = this.zoomToCurrentFrame.bind(this)
        this.on("dataChanged", this.boundZoomToCurrentFrame)
        this.zoomToCurrentFrame()
        return () => {
            this.off("dataChanged", this.boundZoomToCurrentFrame!)
            this.mapRef = undefined
        }
    }

    private boundZoomToCurrentFrame?: typeof this.zoomToCurrentFrame
    private zoomToCurrentFrame() {
        const currentFrameData = this.getCurrentFrameData()
        if (!this.mapRef || !currentFrameData) return
        this.mapRef.flyTo({
            center: [currentFrameData.longitude, currentFrameData.latitude],
        })
    }

    isStreamFinished() {
        return this.streamIsFinished
    }
}
